import { Link } from "lucide-react";
import { marked } from "marked";
import React, { memo, useMemo } from "react";
import ReactMarkdown from 'react-markdown';
import { PreBlock } from "./pre-block";
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import '@/styles/markdown.css';
import { useUser } from "@/components/providers/user-context";

interface Source {
  title: string;
  url: string;
}

interface SourcesData {
  sources: Source[];
}

export interface FileBlock {
  name: string;
  content: string;
  language: string;
}

// Process file block syntax - function to transform triple backtick code blocks with filename
function processFileBlocks(markdown: string): string {
  // Match code blocks with name attribute: ```language name=filename.ext
  const codeBlockRegex = /```(\w+)\s+name=([^\s]+)([\s\S]*?)```/g;
  
  return markdown.replace(codeBlockRegex, (match, language, filename, content) => {
    return '```' + language + ' name=' + filename + content + '```';
  });
}

// Extract file blocks from markdown content for Canvas rendering
export function extractFileBlocks(content: string): FileBlock[] {
  const fileBlocks: FileBlock[] = [];
  
  // First, try to match code blocks with name attribute: ```language name=filename.ext
  // Also match four backticks style: ````language name=filename.ext
  const namedBlockRegex = /(?:```|````)([\w-]+)\s+name=([^\s]+)([\s\S]*?)(?:```|````)/g;
  
  // First pass: look for explicitly named blocks
  let match;
  while ((match = namedBlockRegex.exec(content)) !== null) {
    const language = match[1];
    const filename = match[2];
    // Remove leading newline if present
    const blockContent = match[3].startsWith('\n') ? match[3].substring(1) : match[3];
    
    // Only add code blocks with 5 or more lines to the fileBlocks array
    const lineCount = blockContent.split('\n').length;
    if (lineCount >= 5) {
      fileBlocks.push({
        name: filename,
        content: blockContent,
        language: language
      });
    }
  }
  
  // Always look for regular code blocks, not just when named blocks aren't found
  // This helps with APIs that don't add name= attributes to code blocks
  const standardBlockRegex = /```([\w-]+)\n([\s\S]*?)```/g;
  let blockCounter = 1;
  
  while ((match = standardBlockRegex.exec(content)) !== null) {
    const language = match[1];
    // Remove trailing newline if present
    let blockContent = match[2];
    if (blockContent.endsWith('\n')) {
      blockContent = blockContent.substring(0, blockContent.length - 1);
    }
    
    // For standard blocks, check if a similar named block already exists to avoid duplicates
    const isDuplicate = fileBlocks.some(block => block.content.trim() === blockContent.trim());
    
    // Only add code blocks with 5 or more lines to the fileBlocks array if not a duplicate
    const lineCount = blockContent.split('\n').length;
    if (lineCount >= 5 && !isDuplicate) {
      const extension = getExtensionForLanguage(language);
      fileBlocks.push({
        name: `${language}-snippet-${blockCounter}${extension}`,
        content: blockContent,
        language: language
      });
      blockCounter++;
    }
  }
  
  return fileBlocks;
}

// Helper function to get file extension based on language
function getExtensionForLanguage(language: string): string {
  const extensionMap: Record<string, string> = {
    'javascript': '.js',
    'js': '.js',
    'typescript': '.ts',
    'ts': '.ts',
    'jsx': '.jsx',
    'tsx': '.tsx',
    'python': '.py',
    'py': '.py',
    'ruby': '.rb',
    'rust': '.rs',
    'go': '.go',
    'java': '.java',
    'c': '.c',
    'cpp': '.cpp',
    'csharp': '.cs',
    'cs': '.cs',
    'php': '.php',
    'html': '.html',
    'css': '.css',
    'scss': '.scss',
    'json': '.json',
    'yaml': '.yml',
    'markdown': '.md',
    'md': '.md',
    'shell': '.sh',
    'bash': '.sh',
    'sh': '.sh',
    'sql': '.sql',
    'xml': '.xml',
    'swift': '.swift',
    'kotlin': '.kt',
    'dart': '.dart',
    'r': '.r',
    'julia': '.jl',
    'haskell': '.hs',
    'elixir': '.ex',
    'lua': '.lua'
  };

  return extensionMap[language.toLowerCase()] || '.txt';
}

// Handle special case of Markdown files with four backticks
function processFourBacktickMarkdown(markdown: string): string {
  // Match four backticks markdown blocks: ````markdown name=filename.md ... ````
  const fourBackticksRegex = /````(markdown)\s+name=([^\s]+)([\s\S]*?)````/g;
  
  return markdown.replace(fourBackticksRegex, (match, language, filename, content) => {
    // Convert to three backticks for ReactMarkdown processing
    // ReactMarkdown will handle the inner code blocks correctly
    return '```' + language + ' name=' + filename + content + '```';
  });
}

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  // Ensure all tokens are converted to string using `raw` or a default
  return tokens.map(token => token.raw || '');
}

// Extract JSON sources block from markdown if present
function extractSources(content: string): { markdownContent: string, sources: Source[] | null } {
  // First, check for the new API format sources appended as JSON at the end
  const sourcesPattern = /\{(?:\s*)"sources"(?:\s*):(?:\s*)\[([\s\S]*?)\](?:\s*)\}(?:\s*)$/;
  const sourcesMatch = content.match(sourcesPattern);
  
  if (sourcesMatch) {
    try {
      // Fix the JSON format by wrapping it properly
      const jsonStr = `{"sources": [${sourcesMatch[1]}]}`;
      const sources = JSON.parse(jsonStr) as SourcesData;
      
      // Remove the sources JSON from the content
      const markdownContent = content.replace(sourcesPattern, '').trim();
      
      return { markdownContent, sources: sources.sources };
    } catch (e) {
      console.error('Failed to parse appended sources JSON:', e);
    }
  }
  
  // Second, try to match sources in a JSON code block
  const jsonBlockMatch = content.match(/```json\s*(\{\s*"sources"\s*:\s*\[\s*(?:\{\s*"title"\s*:\s*"[^"]*"\s*,\s*"url"\s*:\s*"[^"]*"\s*\}\s*,?\s*)+\s*\]\s*\})\s*```\s*$/);
  
  if (jsonBlockMatch) {
    try {
      const sourcesJson = jsonBlockMatch[1];
      const sources = JSON.parse(sourcesJson) as SourcesData;
      
      // Remove the JSON block from the content
      const markdownContent = content.replace(jsonBlockMatch[0], '').trim();
      
      return { markdownContent, sources: sources.sources };
    } catch (e) {
      console.error('Failed to parse JSON block sources:', e);
    }
  }
  
  // Third option: check if the content contains references field from your new endpoint format
  try {
    const referencesRegex = /"references"\s*:\s*(\[[\s\S]*?\])/;
    const referencesMatch = content.match(referencesRegex);
    
    if (referencesMatch) {
      const referencesJson = referencesMatch[1];
      const references = JSON.parse(referencesJson);
      
      if (Array.isArray(references) && references.length > 0) {
        const sources = references.map(ref => ({
          title: ref.title || 'Reference',
          url: ref.url || 'N/A'
        }));
        
        // Keep the markdown content as is for references
        return { markdownContent: content, sources };
      }
    }
  } catch (e) {
    console.error('Failed to parse references:', e);
  }
    return { markdownContent: content, sources: null };
}

// Custom components for ReactMarkdown
const customComponents = {
  pre: PreBlock,
  // Add any other custom components here
};

const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string; codeTheme?: string }) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          rehypeKatex,
          [rehypeHighlight, {
            detect: true,
            ignoreMissing: true,
            subset: false
          }]
        ]}
        components={customComponents}
      >
        {content}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the content or theme changes
    return prevProps.content === nextProps.content && 
           prevProps.codeTheme === nextProps.codeTheme;
  },
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

const SourcesList = memo(({ sources }: { sources: Source[] }) => {
  if (!sources || sources.length === 0) return null;
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="text-lg font-semibold mb-2">References</h3>
        {sources.map((source, index) => (
            <a 
              key={`${source.url}-${index}`}
              href={source.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline dark:text-blue-300 flex items-center gap-2"
            >
              <Link className="w-4 h-4" />{source.title}
            </a>
        ))}
    </div>
  );
});

SourcesList.displayName = 'SourcesList';

interface ReferencesProps {
  references?: {
    title: string;
    url: string;
  }[];
}

// Helper function to convert references to Source format
function referencesToSources(references: any[] | undefined): Source[] | null {
  if (!references || !Array.isArray(references) || references.length === 0) {
    return null;
  }
  return references.map(ref => ({
    title: ref.title || 'Reference',
    url: ref.url || '#'
  }));
}

export const MemoizedMarkdown = memo(
  ({ content, id, references }: { content: string; id: string; references?: any[] }) => {
    const { preferences } = useUser();

    // Apply font size classes with responsive adjustments
    const fontSizeClasses = {
      small: "text-xs sm:text-sm",
      medium: "text-sm sm:text-base",
      large: "text-base sm:text-lg"
    };

    // Process the content to handle file blocks and four-backtick markdown blocks
    const processedContent = useMemo(() => {
      let processed = content;
      processed = processFourBacktickMarkdown(processed);
      processed = processFileBlocks(processed);
      return processed;
    }, [content]);
    
    // Only look for sources in content if no references were provided directly
    const { markdownContent, sources: extractedSources } = useMemo(() => 
      references ? { markdownContent: processedContent, sources: null } : extractSources(processedContent), 
    [processedContent, references]);
    
    // Use explicitly provided references first, fall back to extracted sources
    const sources = useMemo(() => 
      references ? referencesToSources(references) : extractedSources, 
    [references, extractedSources]);
    
    // Only parse into blocks if we have valid markdown content
    const blocks = useMemo(() => {
      if (!markdownContent) return [];
      return parseMarkdownIntoBlocks(markdownContent);
    }, [markdownContent]);

    return (
      <div className={`markdown-content break-words px-1 sm:px-0 ${fontSizeClasses[preferences.fontSize || 'medium']}`}>
        {blocks.map((block, index) => (
          <MemoizedMarkdownBlock 
            content={block} 
            key={`${id}-block_${index}`}
            codeTheme={preferences.codeTheme || 'github'} 
          />
        ))}
        {sources && <SourcesList sources={sources} />}
      </div>
    );
  },  // Add strict comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return prevProps.id === nextProps.id && 
           prevProps.content === nextProps.content && 
           JSON.stringify(prevProps.references) === JSON.stringify(nextProps.references);
  }
);

MemoizedMarkdown.displayName = 'MemoizedMarkdown';
